// The tiered rewards bar, and the discount code that goes with it.
//
// Replaces Upcart's Tiered Rewards module. Two jobs that have to agree with each other: put the
// right Shopify discount code on the session, and show the customer the same figure they will see
// at checkout. If those two ever disagree the drawer is worse than useless — the customer reaches
// checkout, the number has moved, and they leave.
//
// The code itself is applied by fetching /discount/<CODE>, which is how Shopify sets the
// discount_code cookie. There is no supported cart API for this; the cookie is carried into
// checkout and applied there.

(function () {
  const TIERS = [
    { qty: 2, percent: 10, code: 'EXTRA-10', label: '10% discount' },
    { qty: 3, percent: 15, code: 'EXTRA-15', label: '15% discount' },
    { qty: 4, percent: 20, code: 'EXTRA-20', label: '20% discount' },
    // Not a milestone on the bar — the bar stops at three — but a real code that Shopify applies
    // from five items. Left out, the drawer showed 20% while the cart was charging 25%.
    { qty: 5, percent: 25, code: 'EXTRA-25', label: '25% discount', offBar: true },
  ];
  const COMPLETE_TEXT = 'Buy 5 or more items to get an extra 25% off';
  /** The bar only draws the milestones that are on it. */
const BAR_TIERS = TIERS.filter((t) => !t.offBar);
const MAX_QTY = BAR_TIERS[BAR_TIERS.length - 1].qty;

  const money = (cents) =>
    (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(cents) : '$' + (cents / 100).toFixed(2));

  function reachedTier(count) {
    let reached = null;
    for (const tier of TIERS) if (count >= tier.qty) reached = tier;
    return reached;
  }

  /** The next milestone *on the bar*. The 25% is deliberately excluded: it is not drawn as a
   *  milestone, so once the bar is full the message becomes the completion line rather than
   *  pointing at a reward the bar never showed. */
  function nextTier(count) {
    return BAR_TIERS.find((tier) => count < tier.qty) || null;
  }

  /** Sets the discount cookie. Only ever called when the tier actually changed — every call is a
   *  round trip, and a cart drawer that fires one per keystroke on the quantity field is a drawer
   *  that feels broken. */
  let appliedCode = null;
  async function applyCode(code) {
    if (code === appliedCode) return;
    appliedCode = code;
    if (!code) return;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET', credentials: 'same-origin' });
    } catch (error) {
      // A failed apply must not take the drawer down with it. The customer still sees the cart; the
      // worst case is that the code lands at checkout instead.
      console.warn('[cart-rewards] could not apply', code, error);
    }
  }

  /** The last count the bar was painted with, so a click can work out the next one without asking. */
  let painted = 0;

  /** The bar, from a quantity alone. Kept separate from render() so a click can move it in the same
   *  frame: the theme waits for Shopify before it redraws anything, and a rewards bar that only
   *  moves after that round trip reads as broken rather than as slow. The server's answer arrives a
   *  moment later and corrects this if it disagreed. */
  function paintBar(count) {
    const root = document.querySelector('[data-cart-rewards]');
    if (!root) return null;
    painted = count;

    const reached = reachedTier(count);
    const next = nextTier(count);

    root.querySelector('[data-rewards-message]').textContent = next
      ? `Add ${next.qty - count} more item${next.qty - count === 1 ? '' : 's'} to get an extra ${next.percent}% off`
      : COMPLETE_TEXT;

    // The track is painted directly rather than by sizing a child element over it. A separate fill
    // div stayed invisible through several rounds that all read as correct, so there is now nothing
    // to sit behind, be clipped by, or lose a stylesheet rule: one element, one background.
    // The fill lands exactly on a milestone when its quantity is reached, and interpolates evenly
    // between them on the way. Filling on count/MAX_QTY instead put the bar at half way on two
    // items while the first milestone sits at a third, so the colour and the dots disagreed at
    // every step.
    let pct = 100;
    let fromQty = 0;
    let fromPct = 0;
    for (let i = 0; i < BAR_TIERS.length; i++) {
      const atPct = ((i + 1) / BAR_TIERS.length) * 100;
      if (count < BAR_TIERS[i].qty) {
        const span = BAR_TIERS[i].qty - fromQty;
        pct = fromPct + ((count - fromQty) / span) * (atPct - fromPct);
        break;
      }
      fromQty = BAR_TIERS[i].qty;
      fromPct = atPct;
    }
    pct = Math.max(0, Math.min(100, pct));
    const track = root.querySelector('[data-rewards-track]');
    if (track) {
      track.style.background = `linear-gradient(to right, #5c4033 0 ${pct}%, #e2e2e2 ${pct}% 100%)`;
    }

    root.querySelectorAll('[data-tier-qty]').forEach((el) => {
      el.dataset.reached = String(count >= Number(el.dataset.tierQty));
    });

    return root;
  }

  function render(cart) {
    const count = cart.item_count || 0;
    const root = paintBar(count);
    if (!root) return;
    const reached = reachedTier(count);

    // Shopify's own figure, never a percentage worked out here.
    //
    // cart.total_price is the total *after* the code has been applied, so taking a percentage of it
    // double-counted the discount — a cart charging 25% off showed a saving 20% larger still, and
    // the number moved the moment the customer reached checkout. total_discount is what Shopify
    // actually took off, so the drawer and the checkout cannot disagree.
    const savings = cart.total_discount || 0;
    const savingsRow = document.querySelector('[data-cart-savings]');
    if (savingsRow) {
      savingsRow.hidden = savings <= 0;
      if (savings > 0) {
        const applied = (cart.cart_level_discount_applications || [])[0];
        savingsRow.querySelector('[data-savings-code]').textContent = applied ? applied.title : reached.code;
        savingsRow.querySelector('[data-savings-amount]').textContent = `- ${money(savings)}`;
      }
    }

    // The checkout button lives in the half of the footer the server no longer redraws, so its
    // total is set here alongside the saving it belongs to.
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout && typeof cart.total_price === 'number') {
      checkout.textContent = `Checkout \u2022 ${money(cart.total_price)}`;
    }

    applyCode(reached ? reached.code : null);
  }

  async function refresh() {
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      render(cart);
    } catch (error) {
      console.warn('[cart-rewards] could not read the cart', error);
    }
  }

  // Every route that changes a cart goes through /cart/*.js, so watching fetch catches the theme's
  // own updates, the quantity steppers and anything an app does — without depending on a particular
  // theme's events, which differ per theme and change between versions.
  //
  // The reply to that request already contains the new cart, so it is read from there rather than
  // asked for again. Fetching /cart.js on top of it meant two round trips for one click, and the
  // stepper visibly hung for the second one.
  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    const promise = nativeFetch.apply(this, args);
    if (url && /\/cart\/(add|change|update|clear)/.test(url)) {
      promise
        .then((response) => response.clone().json())
        .then(async (cart) => {
          // A change made anywhere else — the remove button, a product page — still has to move the
          // tier. Nothing is painted here: if the code changes, the drawer is rendered from the
          // server, which is the only place the whole cart is stated at once.
          const count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
          if (count === null) return;
          const reached = tierFor(count);
          const wanted = reached ? reached.code : null;
          if (wanted === appliedCode) return;
          await applyCode(wanted);
          const items = document.querySelector('cart-drawer-items');
          if (items && typeof items.onCartUpdate === 'function') await items.onCartUpdate();
          await refresh();
        })
        .catch(() => {});
    }
    return promise;
  };

  /**
   * The stepper, done in one go.
   *
   * Left to the theme, pressing plus does this: the browser steps the number in the box, that fires
   * a change event, the change asks Shopify, and only when Shopify answers does the rest of the
   * drawer catch up. So the quantity jumped to 3 on its own while the price, the bar and the code
   * beside it still said 2, and a moment later everything else moved.
   *
   * Worse, the code in the footer went out on the way. Between "Shopify has the new quantity" and
   * "the new tier has been applied" the cart is priced under a discount that may no longer qualify,
   * and a drawer rendered in that gap shows no saving at all.
   *
   * So the whole sequence is done here and nothing is drawn until it is finished: change the
   * quantity, work out the tier it lands on, apply that code, and only then ask the theme to render
   * the drawer once. The number, the price, the bar and the code all arrive together, because they
   * are all read from the same cart.
   */
  async function step(button) {
    const stepper = button.closest('.uc-qty');
    const input = stepper && stepper.querySelector('.quantity__input');
    if (!input) return;

    const line = input.dataset.index;
    const current = parseInt(input.value, 10);
    if (!Number.isFinite(current)) return;

    // Zero is how a line is removed, so it is a valid destination; below that is not.
    const target = Math.max(0, current + (button.name === 'plus' ? 1 : -1));
    if (target === current) return;

    const items = document.querySelector('cart-drawer-items');
    stepper.dataset.busy = button.name;
    if (items && typeof items.enableLoading === 'function') items.enableLoading(line);

    try {
      // Goes through the queue in cart-queue.js, so two quick presses do not race and a throttled
      // one is retried rather than dropped.
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line: Number(line), quantity: target }),
      });
      const cart = await response.json();
      if (cart && typeof cart.item_count === 'number') {
        const reached = tierFor(cart.item_count);
        await applyCode(reached ? reached.code : null);
      }
    } catch (error) {
      console.warn('[cart-rewards] could not change the quantity', error);
    } finally {
      // Two things to redraw, because they live in different places. onCartUpdate replaces what the
      // theme owns — the rows and the footer — straight from the server. The rewards bar sits above
      // both and is in neither, so it is painted from the cart itself. Both happen after everything
      // has settled, so they still land as one change on screen.
      if (items && typeof items.onCartUpdate === 'function') await items.onCartUpdate();
      await refresh();
      if (items && typeof items.disableLoading === 'function') items.disableLoading(line);
      delete stepper.dataset.busy;
    }
  }

  // Caught before the theme's own handler and stopped there, so the number in the box is never
  // stepped ahead of the cart it is supposed to be showing.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target && event.target.closest && event.target.closest('.uc-qty .quantity__button');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      step(button);
    },
    true,
  );

  document.addEventListener('DOMContentLoaded', refresh);
  if (document.readyState !== 'loading') refresh();
})();
